import { Injectable } from '@angular/core';
import {
  FieldDataUpsertOperationInterface,
  ListAuxiliaryDataRequestInterface,
  ListAuxiliaryDataResponseInterface,
  PagedRequestOptionsInterface,
  SearchByAuxiliaryDataResponseInterface,
  UpsertAuxiliaryDataRequestInterface,
} from '@vendasta/account-group'; // (mostly) identical defs from shared protos, but exported again from each SDK, so some of these are just from the AG SDK
import { Observable, of } from 'rxjs';
import { AbstractAuxiliaryDataSchemaService } from './_abstract.service';
import { AccountGroupService } from './_account-group-data.service';
import { UserService } from './_user-data.service';
import { ProductService } from './_product-data.service';
import { map } from 'rxjs/operators';
import { ObjectSchema } from '../auxiliary-data-table/auxiliary-data-table.interface';
import { AuxiliaryDataFilterInterface } from '@vendasta/account-group/lib/_internal/interfaces/api.interface';
import { ObjectType } from '../schema-service/schema-interface';
import { OrderService } from './_order-data.service';
import { TaskService } from './_task-data.service';

@Injectable({
  providedIn: 'root',
})
export class FieldDataService {
  constructor(
    private readonly accountService: AccountGroupService,
    private readonly userService: UserService,
    private readonly productService: ProductService,
    private readonly orderService: OrderService,
    private readonly taskService: TaskService,
  ) {}

  listData(
    partnerId: string,
    objectType: ObjectType,
    objectId: string,
    paging: PagedRequestOptionsInterface,
  ): Observable<ListAuxiliaryDataResponseInterface> {
    if (!partnerId || !objectType || !objectId) {
      throw new Error(
        `Bad request to list field data. (partner ID: ${partnerId}, object type: ${objectType}, object ID: ${objectId})`,
      );
    }
    const request: ListAuxiliaryDataRequestInterface = {
      auxiliaryDataObjectId: {
        partnerId: partnerId,
        objectId: objectId,
      },
      pagingOptions: paging,
    };
    return this.selectService(objectType).listData(request);
  }

  upsertData(
    partnerId: string,
    objectType: ObjectType,
    objectId: string,
    data: FieldDataUpsertOperationInterface[],
  ): Observable<null> {
    const request: UpsertAuxiliaryDataRequestInterface = {
      auxiliaryDataObjectId: {
        partnerId: partnerId,
        objectId: objectId,
      },
      fieldDataUpsertOperations: data,
    };
    return this.selectService(objectType).upsertData(request);
  }

  searchByAuxiliaryData(
    partnerId: string,
    objectType: ObjectType,
    filter: AuxiliaryDataFilterInterface,
    pagingOptions: PagedRequestOptionsInterface,
  ): Observable<SearchByAuxiliaryDataResponseInterface> {
    if (!this.selectService(objectType).searchByAuxiliaryData) {
      console.warn('Unsupported object type for search by auxiliary data');
      return of({});
    }

    return this.selectService(objectType).searchByAuxiliaryData({
      partnerId,
      filter,
      pagingOptions,
    });
  }

  hasCustomFields(partnerId: string, auxiliaryDataObjectType: ObjectType, productId: string): Observable<boolean> {
    return this.listData(partnerId, auxiliaryDataObjectType, productId, { cursor: '', pageSize: 100 }).pipe(
      map((fields) => {
        const objectSchema = JSON.parse(fields.jsonSchema) as ObjectSchema;
        return Object.keys(objectSchema.properties)?.length > 0;
      }),
    );
  }

  private selectService(objectType: ObjectType): AbstractAuxiliaryDataSchemaService {
    switch (objectType) {
      case 'business':
        return this.accountService;
      case 'user':
        return this.userService;
      case 'product':
        return this.productService;
      case 'order':
        return this.orderService;
      case 'task':
        return this.taskService;
      default:
        throw new Error(`Unsupported object type: ${objectType}`);
    }
  }
}
